import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui';
import { formatMoney, formatQuantity } from '../../lib/format';
import { links } from '../../lib/links';
import { useSession } from '../../lib/session';
import { KpiStrip, WidgetCard, type Kpi } from './primitives';
import { QuickActions, type QuickAction } from './QuickActions';
import { HoldQueueWidget } from './widgets/HoldQueueWidget';
import { LedgerIntegrityWidget } from './widgets/LedgerIntegrityWidget';
import { LowStockWidget } from './widgets/LowStockWidget';
import { RecentAuditWidget } from './widgets/RecentAuditWidget';
import { SystemStatusWidget } from './widgets/SystemStatusWidget';
import {
  lowStockQuery,
  onHoldQuery,
  openBatchesQuery,
  roleCountQuery,
  stockValuationQuery,
  userCountQuery,
} from './queries';

const OPEN_STATUSES = new Set(['draft', 'in_progress', 'completed']);

/**
 * The control tower. An administrator needs to know the ledger is trustworthy
 * and who has access, alongside the operational picture — so integrity and
 * access sit next to the same numbers everyone else sees, without taking over.
 */
export function AdminDashboard() {
  const { can } = useSession();

  const valuation = useQuery(stockValuationQuery);
  const lowStock = useQuery(lowStockQuery);
  const board = useQuery(openBatchesQuery);
  const hold = useQuery(onHoldQuery);
  const users = useQuery(userCountQuery);
  const roles = useQuery(roleCountQuery);

  const lowCount = lowStock.data?.items.length;
  const holdCount = hold.data?.items.length;
  const openBatches = (board.data?.items ?? []).filter((batch) => OPEN_STATUSES.has(batch.status)).length;

  const kpis: Kpi[] = [
    {
      label: 'Stock value',
      value: valuation.data ? formatMoney(valuation.data.totals.quantityValue) : '—',
      to: '/reports',
    },
    { label: 'Low stock', value: lowCount === undefined ? '—' : String(lowCount), tone: lowCount ? 'warn' : 'good' },
    { label: 'On hold', value: holdCount === undefined ? '—' : String(holdCount), to: '/quality' },
    { label: 'Open batches', value: board.isSuccess ? String(openBatches) : '—', to: '/batches' },
  ];

  const actions: QuickAction[] = [
    { label: 'Inventory', to: links.inventoryBalances() },
    { label: 'Batches', to: '/batches' },
    { label: 'Quality', to: '/quality' },
    { label: 'Audit', to: '/audit', capability: 'audit.read' },
    { label: 'Users', to: '/users', capability: 'users.manage' },
    { label: 'Roles', to: '/roles', capability: 'users.manage' },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Control tower"
        description="The ledger's integrity, who can reach what, and the operational picture across inventory, production and quality."
      />

      <KpiStrip items={kpis} />

      <div className="mt-4">
        <QuickActions actions={actions} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <LedgerIntegrityWidget />
        <SystemStatusWidget />
        <WidgetCard title="Access" description="People and the roles they can hold.">
          <dl className="grid grid-cols-2 gap-3">
            <div>
              <dt className="text-xs font-bold uppercase tracking-widest text-ink-muted">Users</dt>
              <dd className="mt-1 text-xl font-semibold text-ink">
                {users.data ? formatQuantity(String(users.data.total)) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-bold uppercase tracking-widest text-ink-muted">Roles</dt>
              <dd className="mt-1 text-xl font-semibold text-ink">
                {roles.data ? String(roles.data.total) : '—'}
              </dd>
            </div>
          </dl>
        </WidgetCard>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LowStockWidget to={links.inventoryBalances()} />
        <HoldQueueWidget canDecide={can('qc.release')} to="/quality" />
      </div>

      <div className="mt-4">
        <RecentAuditWidget limit={8} to="/audit" />
      </div>
    </>
  );
}
